'use client';

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowRight } from 'lucide-react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { fadeUp, viewportOnce, scrollJackOffset } from '@/lib/motion';
import { SectionHeading, Button } from '@/components/ui';
import s from './About.module.css';

/* About Us — content lives in the const arrays below (copy from the approved
   About design). Layout/scroll effects are page-local; see About.module.css. */

const HERO = {
  titleLead: 'A million voices.',
  titleAccent: 'One at a time',
  alt: 'The Locobuzz team together in the Mumbai office',
  copy: 'Every day a brand is spoken to far more times than it can possibly answer. We build the platform that closes that gap — so someone who reaches out at 2am on a Tuesday gets a reply that sounds like a person actually read it.',
};

const FACTS = [
  { value: '170+', label: 'People' },
  { value: '4', label: 'Offices' },
  { value: '12', label: 'Roles open now' },
  { value: '2015', label: 'Founded' },
];

const WHAT = {
  label: 'What we do',
  title: 'One platform for every conversation a brand is part of.',
  copy: 'Locobuzz is a unified customer experience platform. We pull every signal a brand generates — social, messaging, email, chat, voice, app stores, review sites — into a single view, then use AI to sort it, route it, and give the person answering it the context they need.',
  footStrong: 'Over 350 enterprise brands across BFSI, telecom, aviation, retail, hospitality and automotive run on Locobuzz today',
  foot: ' — including more than 50 Fortune 500 companies. We’re headquartered in Mumbai and growing across Southeast Asia, the Middle East and the US.',
};

const VISION = {
  tag: 'Vision',
  head: 'A world where size is no longer an excuse for being ignored — where every digital handshake feels as warm as a real one.',
  body: 'We want to get to a point where "they never got back to me" sounds as dated as a busy signal. Not because a machine replied, but because the machine made room for a person to.',
};
const MISSION = {
  tag: 'Mission',
  head: 'Give every brand one place to hear, understand and answer its customers, at the speed the internet actually moves.',
  body: 'That means listening everywhere a customer might speak, reading intent before a human has to, and handing the person who replies enough context to sound like they already knew.',
};

const VALUES = [
  { n: '01', word: 'Empathy', body: 'Every message in the queue is a person having a bad or a good day. We build for them, not for the ticket.' },
  { n: '02', word: 'Listening', body: 'The answer is usually already in what customers said. We read it before we argue about it.' },
  { n: '03', word: 'Continuous learning', body: 'Nobody here is finished. Budgets, mentors and time are set aside for that on purpose.' },
  { n: '04', word: 'Collaboration', body: 'Care, product and marketing look at the same queue, so the argument is about what to do next.' },
  { n: '05', word: 'Innovation', body: 'We would rather test the awkward new idea than polish the safe old one.' },
  { n: '06', word: 'Integrity', body: 'Honest timelines, honest numbers, honest answers, including when they cost us.' },
];

const LEADERS = [
  {
    name: 'Shubhii Agarawal', role: 'Co-founder & COO', img: '/about/founder-shubhii.webp',
    quote: '“We didn’t set out to build a dashboard. We set out to make sure the person on the other end of a message doesn’t get treated like a ticket number. Everything we’ve shipped since has been judged against that.”',
  },
  {
    name: 'Vishal Agarawal', role: 'Co-founder & CEO', img: '/about/founder-vishal.webp',
    quote: '“The hard part was never collecting the data. It was making it useful inside the thirty seconds a support agent actually has. That constraint has shaped our entire architecture.”',
  },
  {
    name: 'Nitinn Aggarwal', role: 'Co-founder & CTO', img: '/about/founder-nitinn.webp',
    quote: '“We’ve grown up alongside our customers rather than ahead of them. Most of what’s good in the product came from a client telling us, plainly, that something wasn’t working.”',
  },
];

const CAREERS = {
  eyebrow: 'Careers',
  title: 'The next few years get built by whoever joins now.',
  copy: 'We’re hiring across engineering, data science, customer success and sales — in Mumbai and remotely. If the idea of making millions of conversations feel personal sounds like a good problem, come take a look.',
  cta: 'View open roles',
};

const smooth = (t) => t * t * (3 - 2 * t);
const clamp01 = (n) => Math.max(0, Math.min(1, n));

export default function AboutClient() {
  const reduced = useReducedMotion();

  /* What-we-do: cover image slides up over the headline as the section scrolls. */
  const whatRef = useRef(null);
  const { scrollYProgress: whatP } = useScroll({ target: whatRef, offset: scrollJackOffset });
  const coverY = useTransform(whatP, [0.12, 0.52], ['103%', '0%'], { ease: smooth });

  /* Mission & Vision: vision card shrinks away while mission grows into place. */
  const vmRef = useRef(null);
  const { scrollYProgress: vmP } = useScroll({ target: vmRef, offset: scrollJackOffset });
  const visionScale = useTransform(vmP, [0.12, 0.62], [1, 0.42], { ease: smooth });
  const missionScale = useTransform(vmP, [0.12, 0.62], [0.42, 1], { ease: smooth });
  const swap = useTransform(vmP, (p) => smooth(clamp01((p - 0.12) / 0.5)) > 0.5);
  const visionZ = useTransform(swap, (on) => (on ? 2 : 3));
  const missionZ = useTransform(swap, (on) => (on ? 3 : 2));

  /* Fit the fixed 1200x660 vision/mission canvas to the available space. */
  const fitWrapRef = useRef(null);
  const [fit, setFit] = useState({ scale: 1, height: 660 });
  useEffect(() => {
    const el = fitWrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const scale = Math.min(1, el.clientWidth / 1200, (window.innerHeight - 180) / 660);
      setFit({ scale, height: Math.round(660 * scale) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const reveal = { variants: fadeUp, initial: 'hidden', whileInView: 'show', viewport: viewportOnce };

  return (
    <div className={clsx(s.root, reduced && s.rm)}>
      {/* Hero */}
      <section className={s.hero} aria-label="About Locobuzz">
        <div className={s.wrap}>
          <div className={s.heroInner}>
            <h1 className={clsx('type-section-title-lg', s.heroTitle)}>
              {HERO.titleLead}{' '}
              <span className="serif-accent type-section-accent">{HERO.titleAccent}</span>
            </h1>
            <img
              className={s.heroImg}
              src="/about/hero.webp"
              srcSet="/about/hero-960.webp 960w, /about/hero-1600.webp 1600w, /about/hero.webp 2400w"
              sizes="(max-width: 1232px) 100vw, 1200px"
              alt={HERO.alt}
              width="2400" height="1800"
              fetchPriority="high"
              decoding="async"
            />
            <p className={s.heroCopy}>{HERO.copy}</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className={s.section} style={{ paddingTop: 0 }} aria-label="Locobuzz at a glance">
        <div className={s.wrap}>
          <div className={s.statsBand}>
            <div className={s.statsGrid} aria-hidden="true" />
            <div className={s.statsRow}>
              {FACTS.map((f) => (
                <div key={f.label} className={s.stat}>
                  <div className={s.statValue}>{f.value}</div>
                  <div className={s.statLabel}>{f.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className={clsx(s.section, s.alt)}>
        <div className={s.wrap}>
          <div ref={whatRef} className={s.whatTrack}>
            <div className={s.whatSticky}>
              <SectionHeading eyebrow={WHAT.label} align="center" />
              <div className={s.whatStage}>
                <img className={s.fill} src="/about/what-bg.webp" alt="" loading="lazy" decoding="async" />
                <h2 className={s.whatTitle}>{WHAT.title}</h2>
                <motion.div className={s.cover} style={reduced ? undefined : { y: coverY }}>
                  <img className={s.fill} src="/about/what-cover.webp" alt="" loading="lazy" decoding="async" />
                  <p className={s.coverCopy}>{WHAT.copy}</p>
                </motion.div>
              </div>
            </div>
          </div>
          <motion.p className={s.whatFoot} {...reveal}>
            <strong style={{ fontWeight: 700 }}>{WHAT.footStrong}</strong>{WHAT.foot}
          </motion.p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className={s.section} style={{ paddingBottom: 0 }}>
        <div className={s.wrap}>
          <SectionHeading eyebrow="Mission & Vision" align="center" />
        </div>
        <div ref={vmRef} className={s.vmTrack}>
          <div className={s.vmSticky}>
            <div className={clsx(s.wrap, s.vmInner)}>
              <div ref={fitWrapRef} className={s.vmFitWrap} style={{ height: fit.height }}>
                <div className={s.vmFit} style={{ transform: `scale(${fit.scale})` }}>
                  <div className={s.vmBg}>
                    <img src="/about/vision-bg.webp" alt="" loading="lazy" decoding="async" />
                  </div>
                  <motion.div
                    className={clsx(s.vmCard, s.vmVision)}
                    style={reduced ? undefined : { scale: visionScale, zIndex: visionZ }}
                  >
                    <div className={s.vmShadow} />
                    <div className={s.vmBody}>
                      <div className={clsx('serif-accent', s.vmTag)}>{VISION.tag}</div>
                      <div className={s.vmHead}>{VISION.head}</div>
                      <p className={s.vmText}>{VISION.body}</p>
                    </div>
                  </motion.div>
                  <motion.div
                    className={clsx(s.vmCard, s.vmMission)}
                    style={reduced ? undefined : { scale: missionScale, zIndex: missionZ }}
                  >
                    <div className={s.vmShadow} />
                    <div className={s.vmBody}>
                      <div className={clsx('serif-accent', s.vmTag)}>{MISSION.tag}</div>
                      <div className={s.vmHead}>{MISSION.head}</div>
                      <p className={s.vmText}>{MISSION.body}</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className={clsx(s.section, s.alt)}>
        <div className={s.wrap}>
          <div className={s.head}>
            <SectionHeading eyebrow="Our values" title="Six words we" titleAccent="actually argue about." />
            <p className={s.lede}>Not a poster in the lobby. These are the tie-breakers when a decision is genuinely close.</p>
          </div>
          <div className={s.valuesGrid}>
            {VALUES.map((v, i) => (
              <div key={v.word} className={s.value}>
                <div className={s.valueTop}>
                  <div className={clsx(s.gfx, s[`g${i + 1}`])} aria-hidden="true"><span /><span /><span /></div>
                  <div className={s.valueNum}>{v.n}</div>
                </div>
                <div className={s.valueWord}>{v.word}</div>
                <div className={s.valueBody}>{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className={s.section}>
        <div className={s.wrap}>
          <div className={s.head}>
            <SectionHeading eyebrow="Leadership" title="Three founders," titleAccent="one stubborn idea." />
            <p className={s.lede}>
              Locobuzz started in 2015 with a shared frustration: brands were getting louder and listening less. A decade on, the founders still sit close to the product and closer to the customers.
            </p>
          </div>
          <div className={s.leadersGrid}>
            {LEADERS.map((p) => (
              <article key={p.name} className={s.leader}>
                <div className={s.leaderPhoto}>
                  <img src={p.img} alt={p.name} loading="lazy" decoding="async" />
                </div>
                <div className={s.leaderBody}>
                  <div className={s.leaderName}>{p.name}</div>
                  <div className={s.leaderRole}>{p.role}</div>
                  <p className={s.leaderQuote}>{p.quote}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Founder-first */}
      <section className={clsx(s.section, s.alt)}>
        <div className={s.wrap}>
          <SectionHeading title="Founder-first," titleAccent="people-first" size="sm" />
          <p className={s.founderP}>
            Our founders sit close enough to be interrupted. Ideas, questions and disagreements go straight to them — not through three layers first.
          </p>
        </div>
      </section>

      {/* Careers CTA */}
      <section className={s.section}>
        <div className={s.wrap}>
          <div className={s.ctaBox}>
            <div>
              <div className={s.ctaEyebrow}>{CAREERS.eyebrow}</div>
              <h2 className={clsx('type-section-title', s.ctaH)}>{CAREERS.title}</h2>
            </div>
            <div>
              <p className={s.ctaP}>{CAREERS.copy}</p>
              <Button href="/careers" variant="cta" size="lg" iconRight={<ArrowRight size={18} aria-hidden="true" />}>
                {CAREERS.cta}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
